import { Phone } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/motion";
import { PhaseSteps } from "@/components/site/PhaseSteps";
import { Notice112 } from "@/components/site/Notice112";
import { PhotoFrame } from "@/components/site/Placeholders";
import { Icon } from "@/components/site/Icon";
import { Btn } from "@/components/site/Buttons";
import { CtaBand } from "@/components/site/CtaBand";
import { PHONE, PHONE_HREF } from "@/data/site";

const TOOLS = [
  { icon: "Headset", t: "Operatori dedicati", d: "Persone formate che ascoltano, valutano e seguono ogni richiesta." },
  { icon: "MonitorSmartphone", t: "Strumenti digitali", d: "Strumenti che aiutano a registrare, pianificare e condividere le informazioni." },
  { icon: "ListChecks", t: "Gestione delle richieste", d: "Sistemi per tracciare ogni richiesta dalla ricezione alla conclusione." },
  { icon: "Sparkles", t: "Supporto dell'intelligenza artificiale", d: "Tecnologie basate sull'IA che aiutano gli operatori a organizzare il lavoro." },
  { icon: "Network", t: "Rete territoriale di partner", d: "Partner qualificati distribuiti sul territorio, coordinati dalla Centrale." },
  { icon: "CalendarClock", t: "Pianificazione", d: "Programmazione di servizi singoli, ricorrenti e dedicati alle strutture." },
];

export default function CentraleOperativa() {
  return (
    <div data-testid="centrale-page">
      <PageHero
        eyebrow="Centrale Operativa"
        lines={["Il cuore", "organizzativo di Nexus."]}
        intro="La Centrale Operativa riceve le richieste, pianifica, individua il partner più adatto e coordina il servizio. È il punto in cui competenze, tecnologia e rete territoriale lavorano insieme."
      >
        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
          <Btn href={PHONE_HREF} testId="centrale-hero-phone-btn">Chiama {PHONE}</Btn>
          <span className="inline-flex items-center gap-3 rounded-full border border-gold/50 bg-white px-5 py-3 text-sm font-semibold text-navy" data-testid="centrale-h24-badge">
            <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-70" /><span className="relative h-2.5 w-2.5 rounded-full bg-gold" /></span>
            Reperibilità H24 secondo le modalità di servizio previste
          </span>
        </div>
      </PageHero>

      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8"><Reveal><Notice112 /></Reveal></div>
      </section>

      <section data-testid="centrale-tools" className="bg-white pb-20 md:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><Eyebrow>Come è organizzata</Eyebrow></Reveal>
          <Reveal delay={0.1}><h2 className="mt-6 max-w-3xl text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">Persone, strumenti e una rete, al servizio di ogni richiesta.</h2></Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-[24px] border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-3">
            {TOOLS.map((t, i) => (
              <Reveal key={t.t} delay={(i % 3) * 0.1} className="group bg-white p-8 transition-colors duration-500 hover:bg-mist md:p-10">
                <Icon name={t.icon} className="h-8 w-8 text-gold-ink transition-transform duration-500 group-hover:-translate-y-1" />
                <h3 className="mt-8 text-lg font-semibold text-navy">{t.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate2">{t.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="centrale-phases" className="bg-mist py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><Eyebrow>Il metodo</Eyebrow></Reveal>
          <Reveal delay={0.1}><h2 className="mt-6 max-w-3xl text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">Cinque fasi per ogni servizio.</h2></Reveal>
          <div className="mt-16"><PhaseSteps /></div>
        </div>
      </section>

      <section data-testid="centrale-human-tech" className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <PhotoFrame src="/media/ill-centrale.jpg" label="Un operatore della Centrale Operativa al lavoro" className="aspect-[4/3] rounded-[28px]" testId="centrale-photo" />
          </Reveal>
          <div>
            <Reveal><Eyebrow>Tecnologia e persone</Eyebrow></Reveal>
            <Reveal delay={0.1}><h2 className="mt-6 text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">La tecnologia supporta. <span className="text-gold-ink">Le persone decidono.</span></h2></Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-slate2 md:text-lg">
                Strumenti digitali e tecnologie basate sull'intelligenza artificiale aiutano la Centrale a gestire le richieste in modo ordinato e a pianificare con maggiore precisione.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate2">
                La tecnologia supporta il lavoro umano, senza sostituire la responsabilità degli operatori: ogni valutazione e ogni decisione restano affidate a persone competenti.
              </p>
            </Reveal>
            <Reveal delay={0.3} className="mt-10">
              <a href={PHONE_HREF} data-testid="centrale-phone-card" className="group flex items-center gap-5 rounded-2xl border border-navy/10 p-6 transition-colors hover:border-gold">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-navy"><Phone className="h-5 w-5 text-gold" strokeWidth={1.5} /></span>
                <span>
                  <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-gold-ink">Telefono diretto</span>
                  <span className="mt-1 block text-2xl font-semibold text-navy">{PHONE}</span>
                </span>
              </a>
            </Reveal>
          </div>
        </div>
      </section>
      <CtaBand />
    </div>
  );
}
